import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Property, Unit, PropertyDocument } from '../../types';
import { getPropertyCoverImageUrl, api } from '../../services/api';
import { ImageUpload } from '../ui/ImageUpload';
import { Plus, Trash2, Edit, FileText, ChevronDown, Search, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { ESTADOS_MEXICO, getMunicipiosByEstado } from '../../data/estadosMunicipiosMexico';
import { usePermissions } from '../../hooks/usePermissions';
import { computePlusvaliaPct, formatPlusvaliaPct } from '../../utils/propertyValue';

interface PropertyFormProps {
  property?: Property;
  propertyUnits?: Unit[];
  onSubmit: (property: Omit<Property, 'id' | 'createdAt'>, units?: Omit<Unit, 'id' | 'propertyId'>[]) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const PropertyForm: React.FC<PropertyFormProps> = ({
  property,
  propertyUnits = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const { hasPermission, canCreate, canUpdate } = usePermissions();
  const canDeleteUnits = hasPermission('delete_units');
  const canDeletePropertyDocs = hasPermission('delete_properties');
  const canCreateUnits = canCreate('units');
  const canUpdateUnits = canUpdate('units');
  const isEditingProperty = !!property;
  const canEditUnitRow = isEditingProperty ? canUpdateUnits : canCreateUnits;

  const [formData, setFormData] = useState({
    name: property?.name || '',
    address: property?.address || '',
    city: property?.city || '', // Esto será el municipio
    estado: '', // Estado de México (nuevo campo)
    unitCount: property?.unitCount || 0,
    occupiedUnits: property?.occupiedUnits || 0,
    m2Total: typeof property?.m2Total === 'number' ? property.m2Total : (parseFloat(String(property?.m2Total || 0)) || 0),
    pricePerM2: 0,
    status: property?.status || 'en-proyecto' as 'en-proyecto' | 'en-construccion' | 'en-remodelacion' | 'operativo',
    totalInvestment: property?.totalValue || 0,
    currentValue:
      property?.currentValue != null ? Number(property.currentValue) : 0,
    coverImage: property?.coverImage || '',
  });

  // Estados para los dropdowns
  const [isEstadoDropdownOpen, setIsEstadoDropdownOpen] = useState(false);
  const [estadoSearchTerm, setEstadoSearchTerm] = useState('');
  const estadoDropdownRef = useRef<HTMLDivElement>(null);
  const estadoSearchInputRef = useRef<HTMLInputElement>(null);

  const [isMunicipioDropdownOpen, setIsMunicipioDropdownOpen] = useState(false);
  const [municipioSearchTerm, setMunicipioSearchTerm] = useState('');
  const municipioDropdownRef = useRef<HTMLDivElement>(null);
  const municipioSearchInputRef = useRef<HTMLInputElement>(null);

  const [units, setUnits] = useState<Omit<Unit, 'id' | 'propertyId'>[]>(() => {
    if (propertyUnits.length > 0) {
      return propertyUnits.map(u => ({
        unitNumber: u.unitNumber,
        m2: u.m2,
        propertyType: u.propertyType,
        bedrooms: u.bedrooms,
        bathrooms: u.bathrooms,
        status: u.status,
        tenantId: u.tenantId,
        tenantName: u.tenantName,
        monthlyRent: u.monthlyRent,
        contractStartDate: u.contractStartDate,
        contractEndDate: u.contractEndDate,
        notes: u.notes,
      }));
    }
    return [];
  });

  const [editingUnitIndex, setEditingUnitIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [m2TotalDisplay, setM2TotalDisplay] = useState<string>('');
  const [unitM2Display, setUnitM2Display] = useState<string>('');
  const [totalInvestmentDisplay, setTotalInvestmentDisplay] = useState<string>('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState<string>('');

  const plusvaliaPct = useMemo(
    () => computePlusvaliaPct(formData.totalInvestment, formData.currentValue),
    [formData.totalInvestment, formData.currentValue],
  );
  // Incluir id opcional para que al eliminar un documento se envíe la lista correcta y el backend borre el que corresponde
  const [documents, setDocuments] = useState<(Omit<PropertyDocument, 'id' | 'uploadedAt'> & { id?: string })[]>(() => {
    if (property?.documents && property.documents.length > 0) {
      return property.documents.map(doc => ({
        id: doc.id,
        title: doc.title,
        fileUrl: doc.fileUrl || '',
        fileType: doc.fileType || '',
      }));
    }
    return [];
  });
  const [newDocument, setNewDocument] = useState({
    title: '',
    fileUrl: '',
    fileType: '',
  });
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  const documentProgressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(null);
  const [coverImageUploading, setCoverImageUploading] = useState(false);

  // Limpiar intervalo de progreso al desmontar
  useEffect(() => {
    return () => {
      if (documentProgressIntervalRef.current) {
        clearInterval(documentProgressIntervalRef.current);
        documentProgressIntervalRef.current = null;
      }
    };
  }, []);

  // Cerrar dropdowns cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (estadoDropdownRef.current && !estadoDropdownRef.current.contains(event.target as Node)) {
        setIsEstadoDropdownOpen(false);
        setEstadoSearchTerm('');
      }
      if (municipioDropdownRef.current && !municipioDropdownRef.current.contains(event.target as Node)) {
        setIsMunicipioDropdownOpen(false);
        setMunicipioSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus en el input de búsqueda cuando se abre el dropdown de estados
  useEffect(() => {
    if (isEstadoDropdownOpen && estadoSearchInputRef.current) {
      estadoSearchInputRef.current.focus();
    }
  }, [isEstadoDropdownOpen]);

  // Focus en el input de búsqueda cuando se abre el dropdown de municipios
  useEffect(() => {
    if (isMunicipioDropdownOpen && municipioSearchInputRef.current) {
      municipioSearchInputRef.current.focus();
    }
  }, [isMunicipioDropdownOpen]);

  useEffect(() => {
    if (property) {
      // Debug: ver qué tiene la propiedad
      console.log('Property loaded in form:', property);
      console.log('Property documents:', property.documents);
      console.log('Property state value:', property.state);
      
      // Asegurar que m2Total sea siempre un número
      const m2TotalValue = typeof property.m2Total === 'number' 
        ? property.m2Total 
        : (parseFloat(String(property.m2Total || 0)) || 0);
      
      setFormData({
        name: property.name,
        address: property.address,
        city: property.city,
        estado: property.state || '', // Cargar el estado si existe
        unitCount: property.unitCount,
        occupiedUnits: property.occupiedUnits,
        m2Total: m2TotalValue,
        pricePerM2: 0,
        status: property.status,
        totalInvestment: property.totalValue || 0,
        currentValue:
          property.currentValue != null ? Number(property.currentValue) : 0,
        coverImage: property.coverImage || '',
      });
      // Formatear m2Total correctamente al cargar la propiedad
      if (property.m2Total > 0) {
        const m2Value = typeof property.m2Total === 'string' ? parseFloat(property.m2Total) : property.m2Total;
        const formatted = m2Value % 1 === 0 ? m2Value.toString() : m2Value.toFixed(2);
        setM2TotalDisplay(formatted);
      } else {
        setM2TotalDisplay('');
      }
      setTotalInvestmentDisplay(property.totalValue > 0 ? property.totalValue.toLocaleString('es-MX', { 
        minimumFractionDigits: 0,
        maximumFractionDigits: 2 
      }) : '');
      const cv = property.currentValue != null ? Number(property.currentValue) : 0;
      setCurrentValueDisplay(
        cv > 0
          ? cv.toLocaleString('es-MX', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })
          : '',
      );

      // Cargar documentos - verificar tanto documents como property_documents
      const propertyDocuments = property.documents || (property as any).property_documents || [];
      console.log('Property documents found:', propertyDocuments);
      if (propertyDocuments && propertyDocuments.length > 0) {
        const mappedDocuments = propertyDocuments.map((doc: any) => ({
          id: doc.id,
          title: doc.title || '',
          fileUrl: doc.fileUrl || '',
          fileType: doc.fileType || '',
        }));
        console.log('Setting documents:', mappedDocuments);
        setDocuments(mappedDocuments);
      } else {
        console.log('No documents found, setting empty array');
        setDocuments([]);
      }
    }
  }, [property]);

  // Sincronizar pricePerM2Display con formData.pricePerM2 solo si no está siendo editado
  // (esto se maneja manualmente en el handler)

  useEffect(() => {
    if (propertyUnits.length > 0) {
      setUnits(propertyUnits.map(u => ({
        unitNumber: u.unitNumber,
        m2: u.m2,
        propertyType: u.propertyType,
        bedrooms: u.bedrooms,
        bathrooms: u.bathrooms,
        status: u.status,
        tenantId: u.tenantId,
        tenantName: u.tenantName,
        monthlyRent: u.monthlyRent,
        contractStartDate: u.contractStartDate,
        contractEndDate: u.contractEndDate,
        notes: u.notes,
      })));
    }
  }, [propertyUnits]);

  // Actualizar total de unidades y ocupadas cuando cambian las unidades (m² total es editable a mano)
  useEffect(() => {
    const occupiedCount = units.filter(u => u.status === 'occupied').length;
    setFormData((prev) => ({
      ...prev,
      unitCount: units.length,
      occupiedUnits: occupiedCount,
    }));
  }, [units]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'La dirección es requerida';
    }
    if (!formData.estado.trim()) {
      newErrors.estado = 'El estado es requerido';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'El municipio es requerido';
    }
    if (formData.unitCount < 0) {
      newErrors.unitCount = 'No puede ser negativo';
    }
    if (formData.occupiedUnits < 0) {
      newErrors.occupiedUnits = 'No puede ser negativo';
    }
    if (formData.occupiedUnits > formData.unitCount) {
      newErrors.occupiedUnits = 'No puede exceder el total de unidades';
    }
    if (formData.m2Total < 0) {
      newErrors.m2Total = 'No puede ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddDocument = () => {
    if (newDocument.title.trim() && newDocument.fileUrl) {
      setDocuments([...documents, { ...newDocument }]);
      setNewDocument({ title: '', fileUrl: '', fileType: '' });
    }
  };

  const handleRemoveDocument = (index: number) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleDocumentFileChange = (fileUrl: string, fileType?: string) => {
    setDocumentUploadError(null);
    setNewDocument(prev => ({
      ...prev,
      fileUrl,
      fileType: fileType || 'pdf',
    }));
  };

  const handleDocumentFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setDocumentUploadError('Solo se permiten PDF o imágenes (JPG, PNG).');
      return;
    }
    setDocumentUploadError(null);
    setDocumentUploading(true);
    setDocumentUploadProgress(0);

    // Simular avance de la barra hasta ~90% mientras sube (fetch no expone progreso real)
    if (documentProgressIntervalRef.current) clearInterval(documentProgressIntervalRef.current);
    documentProgressIntervalRef.current = setInterval(() => {
      setDocumentUploadProgress((p) => {
        if (p >= 90) {
          if (documentProgressIntervalRef.current) {
            clearInterval(documentProgressIntervalRef.current);
            documentProgressIntervalRef.current = null;
          }
          return 90;
        }
        return p + 4;
      });
    }, 120);

    try {
      const { fileUrl } = await api.uploadPropertyDocument(file);
      const fileType = file.type === 'application/pdf' ? 'pdf' : file.type.split('/')[1] || 'pdf';
      if (documentProgressIntervalRef.current) {
        clearInterval(documentProgressIntervalRef.current);
        documentProgressIntervalRef.current = null;
      }
      setDocumentUploadProgress(100);
      handleDocumentFileChange(fileUrl, fileType);
      // Breve pausa con la barra al 100% antes de mostrar el icono
      setTimeout(() => {
        setDocumentUploading(false);
        setDocumentUploadProgress(0);
      }, 400);
    } catch (err: any) {
      if (documentProgressIntervalRef.current) {
        clearInterval(documentProgressIntervalRef.current);
        documentProgressIntervalRef.current = null;
      }
      setDocumentUploadError(err.message || 'Error al subir el archivo.');
      setNewDocument(prev => ({ ...prev, fileUrl: '', fileType: '' }));
      setDocumentUploading(false);
      setDocumentUploadProgress(0);
    } finally {
      e.target.value = '';
    }
  };

  const handleCoverImageFileSelect = async (file: File | null) => {
    if (!file) {
      setFormData((prev) => ({ ...prev, coverImage: '' }));
      return;
    }
    setCoverImageUploading(true);
    try {
      const result = await api.uploadPropertyCoverImage(file);
      setFormData((prev) => ({ ...prev, coverImage: result.coverImage }));
    } catch (err) {
      console.error('Error uploading cover image:', err);
      alert('Error al subir la imagen de portada. Intenta de nuevo.');
    } finally {
      setCoverImageUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Preparar datos para enviar, excluyendo campos que no están en el DTO del backend
      const { totalInvestment, currentValue, estado, ...restFormData } = formData;
      // Calcular pricePerM2 basado en totalValue y m2Total
      const calculatedPricePerM2 = formData.m2Total > 0 && totalInvestment > 0 
        ? totalInvestment / formData.m2Total 
        : (property?.pricePerM2 || 0);
      const propertyData = {
        ...restFormData,
        totalValue: totalInvestment, // Costo total
        currentValue: currentValue > 0 ? currentValue : null,
        pricePerM2: calculatedPricePerM2, // Calcular o usar el valor existente
        state: estado, // Mapear estado a state para el backend
        // Asegurar que status esté presente y sea válido
        status: formData.status || 'en-proyecto',
        documents: documents.length > 0 ? documents.map((doc, index) => ({
          id: doc.id || `doc-${Date.now()}-${index}`,
          title: doc.title,
          fileUrl: doc.fileUrl,
          fileType: doc.fileType,
          uploadedAt: (property?.documents?.find((d: any) => d.id === doc.id) as any)?.uploadedAt || new Date().toISOString().split('T')[0],
        })) : undefined,
      };
      onSubmit(propertyData, units);
    }
  };

  const addUnit = () => {
    const newUnit: Omit<Unit, 'id' | 'propertyId'> = {
      unitNumber: '',
      m2: 0,
      propertyType: 'habitacional',
      bedrooms: 1,
      bathrooms: 1,
      status: 'available',
    };
    setUnits([...units, newUnit]);
    const newIndex = units.length;
    setEditingUnitIndex(newIndex);
    setUnitM2Display(''); // Inicializar display vacío para nueva unidad
  };

  const updateUnit = (index: number, field: keyof Omit<Unit, 'id' | 'propertyId'>, value: any) => {
    const updatedUnits = [...units];
    updatedUnits[index] = { ...updatedUnits[index], [field]: value };
    setUnits(updatedUnits);
  };

  // Handler para el campo m² de las unidades que permite decimales
  const handleUnitM2Change = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Permitir vacío
    if (inputValue === '') {
      setUnitM2Display('');
      updateUnit(index, 'm2', 0);
      return;
    }
    
    // Validar que solo contenga números y un punto decimal
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (!regex.test(inputValue)) {
      return; // No actualizar si no coincide con el patrón
    }
    
    // Actualizar el display
    setUnitM2Display(inputValue);
    
    // Convertir a número solo si no termina en punto
    if (!inputValue.endsWith('.')) {
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue) && numericValue >= 0) {
        updateUnit(index, 'm2', numericValue);
      }
    } else {
      // Si termina en punto, guardar la parte numérica sin el punto
      const numericPart = parseFloat(inputValue);
      if (!isNaN(numericPart) && numericPart >= 0) {
        updateUnit(index, 'm2', numericPart);
      }
    }
  };

  // Handler para campos de números enteros (Habitaciones, Baños)
  const handleUnitIntegerChange = (index: number, field: 'bedrooms' | 'bathrooms', e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Permitir vacío
    if (inputValue === '') {
      updateUnit(index, field, 0);
      return;
    }
    
    // Validar que solo contenga números enteros
    const regex = /^[0-9]+$/;
    if (!regex.test(inputValue)) {
      return; // No actualizar si no coincide con el patrón
    }
    
    // Convertir a número entero
    const numericValue = parseInt(inputValue, 10);
    if (!isNaN(numericValue) && numericValue >= 0) {
      updateUnit(index, field, numericValue);
    }
  };

  const removeUnit = (index: number) => {
    setUnits(units.filter((_, i) => i !== index));
    if (editingUnitIndex === index) {
      setEditingUnitIndex(null);
      setUnitM2Display(''); // Limpiar display cuando se elimina la unidad que se estaba editando
    } else if (editingUnitIndex !== null && editingUnitIndex > index) {
      setEditingUnitIndex(editingUnitIndex - 1);
    }
  };

  const saveUnit = (index: number) => {
    const unit = units[index];
    if (!unit.unitNumber.trim()) {
      alert('El número de unidad es requerido');
      return;
    }
    if (unit.m2 <= 0) {
      alert('Los metros cuadrados deben ser mayores a 0');
      return;
    }
    setEditingUnitIndex(null);
    setUnitM2Display(''); // Limpiar display cuando se guarda
  };

  // Filtrar estados
  const filteredEstados = ESTADOS_MEXICO.filter((estado) => {
    const search = estadoSearchTerm.toLowerCase();
    return estado.toLowerCase().includes(search);
  });

  // Filtrar municipios según el estado seleccionado
  const availableMunicipios = formData.estado ? getMunicipiosByEstado(formData.estado) : [];
  const filteredMunicipios = availableMunicipios.filter((municipio) => {
    const search = municipioSearchTerm.toLowerCase();
    return municipio.toLowerCase().includes(search);
  });

  const capitalizeWords = (text: string): string => {
    if (!text) return text;
    
    // Preservar espacios al final
    const trailingSpaces = text.match(/\s*$/)?.[0] || '';
    const textWithoutTrailing = text.trimEnd();
    
    if (!textWithoutTrailing) return text;
    
    return textWithoutTrailing
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ') + trailingSpaces;
  };

  const handleEstadoSelect = (estado: string) => {
    setFormData((prev) => ({
      ...prev,
      estado,
      city: '', // Limpiar municipio cuando cambia el estado
    }));
    setIsEstadoDropdownOpen(false);
    setEstadoSearchTerm('');
  };

  /** Compara municipios ignorando acentos (valor guardado vs opción de lista) */
  const isSameMunicipio = (a: string, b: string) => {
    if (!a || !b) return false;
    const n = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
    return n(a) === n(b);
  };

  const handleMunicipioSelect = (municipio: string) => {
    setFormData((prev) => ({
      ...prev,
      city: municipio,
    }));
    setIsMunicipioDropdownOpen(false);
    setMunicipioSearchTerm('');
  };

  const handleChange = (field: string, value: string | number) => {
    if (field === 'name' && typeof value === 'string') {
      // Capitalizar primera letra de cada palabra para el nombre
      const capitalizedValue = capitalizeWords(value);
      setFormData((prev) => ({
        ...prev,
        [field]: capitalizedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handler específico para campos numéricos que solo aceptan números >= 0
  const handleNumberChange = (field: 'unitCount' | 'occupiedUnits' | 'm2Total', e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Manejo especial para m2Total (permite decimales)
    if (field === 'm2Total') {
      // Permitir vacío
      if (inputValue === '') {
        setM2TotalDisplay('');
        setFormData((prev) => ({
          ...prev,
          [field]: 0,
        }));
        if (errors[field]) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        }
        return;
      }
      
      // Validar que solo contenga números y un punto decimal
      const regex = /^[0-9]*\.?[0-9]*$/;
      if (!regex.test(inputValue)) {
        return; // No actualizar si no coincide con el patrón
      }
      
      // Actualizar el display
      setM2TotalDisplay(inputValue);
      
      // Convertir a número solo si no termina en punto
      if (!inputValue.endsWith('.')) {
        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue) && numericValue >= 0) {
          setFormData((prev) => ({
            ...prev,
            [field]: numericValue,
          }));
        }
      } else {
        // Si termina en punto, guardar la parte numérica sin el punto
        const numericPart = parseFloat(inputValue);
        if (!isNaN(numericPart) && numericPart >= 0) {
          setFormData((prev) => ({
            ...prev,
            [field]: numericPart,
          }));
        }
      }
      
      // Limpiar error
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
      return;
    }
    
    // Para campos enteros (unitCount, occupiedUnits)
    // Permitir vacío (se guardará como 0)
    if (inputValue === '') {
      setFormData((prev) => ({
        ...prev,
        [field]: 0,
      }));
      // Limpiar error del campo cuando se modifica
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
      return;
    }
    
    // Solo permitir números enteros
    const regex = /^[0-9]+$/;
    if (!regex.test(inputValue)) {
      return; // No actualizar si no coincide con el patrón
    }
    
    // Convertir a número
    const numericValue = parseInt(inputValue, 10);
    
    // Si no es un número válido, no hacer nada
    if (isNaN(numericValue)) {
      return;
    }
    
    // Asegurar que sea >= 0
    const finalValue = Math.max(0, numericValue);
    
    setFormData((prev) => ({
      ...prev,
      [field]: finalValue,
    }));
    
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handler para prevenir entrada de caracteres no numéricos en campos enteros
  const handleIntegerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permitir teclas de control
    if (['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
      return;
    }
    // Permitir Ctrl/Cmd + A, C, V, X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }
    // Solo permitir números
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Handler para prevenir entrada de caracteres no numéricos en campos decimales
  const handleDecimalKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permitir teclas de control
    if (['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
      return;
    }
    // Permitir Ctrl/Cmd + A, C, V, X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }
    // Permitir punto decimal (solo uno)
    if (e.key === '.' && !(e.currentTarget as HTMLInputElement).value.includes('.')) {
      return;
    }
    // Solo permitir números
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const applyDecimalMoneyInput = (
    inputValue: string,
    field: 'totalInvestment' | 'currentValue',
    setDisplay: (v: string) => void,
    errorKey: string,
  ) => {
    if (inputValue === '') {
      setDisplay('');
      setFormData((prev) => ({ ...prev, [field]: 0 }));
      if (errors[errorKey]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
      return;
    }
    const cleanValue = inputValue.replace(/,/g, '');
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (!regex.test(cleanValue)) return;
    setDisplay(cleanValue);
    if (!cleanValue.endsWith('.')) {
      const numericValue = parseFloat(cleanValue);
      if (!isNaN(numericValue) && numericValue >= 0) {
        setFormData((prev) => ({ ...prev, [field]: numericValue }));
      }
    } else {
      const numericPart = parseFloat(cleanValue);
      if (!isNaN(numericPart) && numericPart >= 0) {
        setFormData((prev) => ({ ...prev, [field]: numericPart }));
      }
    }
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Handler para el campo costo total que permite decimales
  const handleTotalInvestmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyDecimalMoneyInput(
      e.target.value,
      'totalInvestment',
      setTotalInvestmentDisplay,
      'totalInvestment',
    );
  };

  const handleCurrentValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyDecimalMoneyInput(
      e.target.value,
      'currentValue',
      setCurrentValueDisplay,
      'currentValue',
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: Torre Residencial Norte"
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
        </div>

        {/* Dirección */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dirección <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.address ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: Av. Insurgentes 123"
          />
          {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
        </div>

        {/* Estado (de México) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estado <span className="text-red-500">*</span>
          </label>
          <div className="relative" ref={estadoDropdownRef}>
            <button
              type="button"
              onClick={() => setIsEstadoDropdownOpen(!isEstadoDropdownOpen)}
              className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary flex items-center justify-between ${
                errors.estado ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <span className={formData.estado ? 'text-gray-900' : 'text-gray-400'}>
                {formData.estado || 'Seleccione un estado'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isEstadoDropdownOpen ? 'transform rotate-180' : ''}`} />
            </button>

            {/* Dropdown de estados */}
            {isEstadoDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                {/* Buscador */}
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
                      ref={estadoSearchInputRef}
            type="text"
                      value={estadoSearchTerm}
                      onChange={(e) => setEstadoSearchTerm(e.target.value)}
                      placeholder="Buscar estado..."
                      className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                
                {/* Lista de estados */}
                <div className="max-h-64 overflow-auto">
                  {filteredEstados.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No se encontraron estados
                    </div>
                  ) : (
                    filteredEstados.map((estado) => (
                      <button
                        key={estado}
                        type="button"
                        onClick={() => handleEstadoSelect(estado)}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                          estado === formData.estado ? 'bg-primary/10' : ''
                        }`}
                      >
                        <span className="text-sm text-gray-700">{estado}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {errors.estado && <p className="mt-1 text-sm text-red-500">{errors.estado}</p>}
        </div>

        {/* Municipio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Municipio <span className="text-red-500">*</span>
          </label>
          <div className="relative" ref={municipioDropdownRef}>
            <button
              type="button"
              onClick={() => {
                if (formData.estado) {
                  setIsMunicipioDropdownOpen(!isMunicipioDropdownOpen);
                }
              }}
              disabled={!formData.estado}
              className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary flex items-center justify-between ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              } ${!formData.estado ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            >
              <span className={formData.city ? 'text-gray-900' : 'text-gray-400'}>
                {formData.city || (formData.estado ? 'Seleccione un municipio' : 'Primero seleccione un estado')}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isMunicipioDropdownOpen ? 'transform rotate-180' : ''}`} />
            </button>

            {/* Dropdown de municipios */}
            {isMunicipioDropdownOpen && formData.estado && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                {/* Buscador */}
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      ref={municipioSearchInputRef}
                      type="text"
                      value={municipioSearchTerm}
                      onChange={(e) => setMunicipioSearchTerm(e.target.value)}
                      placeholder="Buscar municipio..."
                      className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                
                {/* Lista de municipios */}
                <div className="max-h-64 overflow-auto">
                  {filteredMunicipios.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No se encontraron municipios
                    </div>
                  ) : (
                    filteredMunicipios.map((municipio) => (
                      <button
                        key={municipio}
                        type="button"
                        onClick={() => handleMunicipioSelect(municipio)}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                          (municipio === formData.city || isSameMunicipio(municipio, formData.city)) ? 'bg-primary/10' : ''
                        }`}
                      >
                        <span className="text-sm text-gray-700">{municipio}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {errors.city && <p className="mt-1 text-sm text-red-500">{errors.city}</p>}
        </div>

        {/* Estado (Status) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="en-proyecto">En Proyecto</option>
            <option value="en-construccion">En Construcción</option>
            <option value="en-remodelacion">En Remodelación</option>
            <option value="operativo">Operativo</option>
          </select>
        </div>

        {/* Valor actual */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Valor actual
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              $
            </span>
            <input
              type="text"
              value={currentValueDisplay}
              onChange={handleCurrentValueChange}
              onKeyDown={handleDecimalKeyDown}
              onBlur={() => {
                if (formData.currentValue > 0) {
                  setCurrentValueDisplay(
                    formData.currentValue.toLocaleString('es-MX', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    }),
                  );
                }
              }}
              onFocus={() => {
                if (formData.currentValue > 0) {
                  setCurrentValueDisplay(formData.currentValue.toString());
                }
              }}
              className={`w-full pl-8 pr-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.currentValue ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ej: 82,000,000"
            />
          </div>
          {errors.currentValue && (
            <p className="mt-1 text-sm text-red-500">{errors.currentValue}</p>
          )}
        </div>

        {/* Plusvalía (calculada) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Plusvalía
          </label>
          <div
            className={`w-full px-3 py-2 rounded-lg border text-base font-semibold ${
              plusvaliaPct == null
                ? 'bg-gray-50 border-gray-200 text-gray-500'
                : plusvaliaPct > 0
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : plusvaliaPct < 0
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700'
            }`}
          >
            {formatPlusvaliaPct(plusvaliaPct)}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Se calcula con valor actual y costo total.
          </p>
        </div>

        {/* Costo total */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Costo total
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              $
            </span>
            <input
              type="text"
              value={totalInvestmentDisplay}
              onChange={handleTotalInvestmentChange}
              onKeyDown={handleDecimalKeyDown}
              onBlur={() => {
                if (formData.totalInvestment > 0) {
                  setTotalInvestmentDisplay(formData.totalInvestment.toLocaleString('es-MX', { 
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2 
                  }));
                }
              }}
              onFocus={() => {
                if (formData.totalInvestment > 0) {
                  setTotalInvestmentDisplay(formData.totalInvestment.toString());
                }
              }}
              className={`w-full pl-8 pr-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.totalInvestment ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ej: 75,000,000"
            />
          </div>
          {errors.totalInvestment && <p className="mt-1 text-sm text-red-500">{errors.totalInvestment}</p>}
          {isEditingProperty && (
            <p className="mt-1 text-xs text-gray-500">
              Al guardar un costo distinto, se recalculan los montos de las inversiones según su %
              de participación (el % no cambia).
            </p>
          )}
        </div>

        {/* Total de Unidades */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Total de Unidades <span className="text-red-500">*</span>
            <span className="ml-2 text-xs text-gray-500">(calculado automáticamente)</span>
          </label>
          <input
            type="text"
            value={formData.unitCount === 0 ? '' : formData.unitCount.toString()}
            disabled={true}
            className="w-full px-3 py-2 bg-gray-50 text-gray-900 border border-gray-300 rounded-lg cursor-not-allowed"
            readOnly
          />
          {errors.unitCount && <p className="mt-1 text-sm text-red-500">{errors.unitCount}</p>}
        </div>

        {/* Unidades Ocupadas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unidades Ocupadas <span className="text-red-500">*</span>
            <span className="ml-2 text-xs text-gray-500">(calculado automáticamente)</span>
          </label>
          <input
            type="text"
            value={formData.occupiedUnits === 0 ? '' : formData.occupiedUnits.toString()}
            disabled={true}
            className="w-full px-3 py-2 bg-gray-50 text-gray-900 border border-gray-300 rounded-lg cursor-not-allowed"
            readOnly
          />
          {errors.occupiedUnits && <p className="mt-1 text-sm text-red-500">{errors.occupiedUnits}</p>}
        </div>

        {/* Metros Cuadrados Totales */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Metros Cuadrados Totales (m²) <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-1">
            Puedes indicar el total de la propiedad; no se sobrescribe al editar unidades.
          </p>
          <input
            type="text"
            inputMode="decimal"
            value={m2TotalDisplay}
            onChange={(e) => handleNumberChange('m2Total', e)}
            onKeyDown={handleDecimalKeyDown}
            onBlur={() => {
              if (formData.m2Total > 0) {
                const v = formData.m2Total;
                setM2TotalDisplay(v % 1 === 0 ? v.toString() : v.toFixed(2));
              }
            }}
            onFocus={() => {
              if (formData.m2Total > 0) {
                setM2TotalDisplay(formData.m2Total.toString());
              }
            }}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.m2Total ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: 450 o 450.5"
          />
          {errors.m2Total && <p className="mt-1 text-sm text-red-500">{errors.m2Total}</p>}
        </div>

      </div>

      {/* Imagen de Portada — subida por multipart; nunca guarda base64 en BD */}
      <div className="relative">
        <ImageUpload
          currentImage={formData.coverImage ? (formData.coverImage.startsWith('data:') || formData.coverImage.startsWith('http') ? formData.coverImage : getPropertyCoverImageUrl(formData.coverImage)) : ''}
          onImageChange={() => {}}
          onFileSelect={handleCoverImageFileSelect}
          label="Imagen de Portada"
        />
        {coverImageUploading && (
          <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center z-10">
            <div className="flex items-center gap-2 text-sm text-primary font-medium">
              <Loader2 className="w-5 h-5 animate-spin" />
              Subiendo imagen…
            </div>
          </div>
        )}
      </div>

      {/* Gestión de Unidades */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Unidades</h3>
            <p className="text-sm text-gray-500 mt-1">
              Agrega y gestiona las unidades de esta propiedad ({units.length} {units.length === 1 ? 'unidad' : 'unidades'})
            </p>
          </div>
          {canCreateUnits && (
            <Button variant="secondary" size="sm" icon={Plus} onClick={addUnit}>
              Agregar Unidad
            </Button>
          )}
        </div>

        {units.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg bg-gray-50">
            <p className="text-gray-500 mb-2">No hay unidades agregadas</p>
            <p className="text-sm text-gray-400">Haz clic en "Agregar Unidad" para comenzar</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {units.map((unit, index) => (
              <div
                key={index}
                className={`p-4 border rounded-lg ${
                  editingUnitIndex === index
                    ? 'border-accent bg-accent/5'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {editingUnitIndex === index ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Número de Unidad *
                      </label>
                      <input
                        type="text"
                        value={unit.unitNumber}
                        onChange={(e) => updateUnit(index, 'unitNumber', e.target.value)}
                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="Ej: 101"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        m² *
                      </label>
                      <input
                        type="text"
                        value={unitM2Display}
                        onChange={(e) => handleUnitM2Change(index, e)}
                        onKeyDown={handleDecimalKeyDown}
                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="Ej: 45.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tipo de Inmueble *
                      </label>
                      <select
                        value={unit.propertyType || 'habitacional'}
                        onChange={(e) => updateUnit(index, 'propertyType', e.target.value)}
                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      >
                        <option value="habitacional">Habitacional</option>
                        <option value="comercial">Comercial</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Habitaciones
                      </label>
                      <input
                        type="text"
                        value={(unit.bedrooms ?? 0) === 0 ? '' : (unit.bedrooms ?? 0).toString()}
                        onChange={(e) => handleUnitIntegerChange(index, 'bedrooms', e)}
                        onKeyDown={handleIntegerKeyDown}
                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="Ej: 2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Baños
                      </label>
                      <input
                        type="text"
                        value={(unit.bathrooms ?? 0) === 0 ? '' : (unit.bathrooms ?? 0).toString()}
                        onChange={(e) => handleUnitIntegerChange(index, 'bathrooms', e)}
                        onKeyDown={handleIntegerKeyDown}
                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="Ej: 2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Estado
                      </label>
                      <select
                        value={unit.status}
                        onChange={(e) => updateUnit(index, 'status', e.target.value)}
                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      >
                        <option value="available">Disponible</option>
                        <option value="occupied">Ocupada</option>
                        <option value="maintenance">Mantenimiento</option>
                        <option value="reserved">Reservada</option>
                      </select>
                    </div>
                    <div className="flex items-end space-x-2">
                      <button
                        type="button"
                        onClick={() => saveUnit(index)}
                        className="px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 text-sm transition-colors"
                      >
                        Guardar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingUnitIndex(null);
                          setUnitM2Display(''); // Limpiar display cuando se cancela
                          if (!property && units[index].unitNumber === '') {
                            removeUnit(index);
                          }
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div>
                        <span className="font-medium text-gray-900">Unidad {unit.unitNumber}</span>
                        <div className="flex items-center space-x-3 mt-1 text-sm text-gray-600">
                          <span>{unit.m2} m²</span>
                          {unit.bedrooms && <span>{unit.bedrooms} hab.</span>}
                          {unit.bathrooms && <span>{unit.bathrooms} baños</span>}
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              unit.status === 'available'
                                ? 'bg-blue-100 text-blue-800'
                                : unit.status === 'occupied'
                                ? 'bg-green-100 text-green-800'
                                : unit.status === 'maintenance'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}
                          >
                            {unit.status === 'available'
                              ? 'Disponible'
                              : unit.status === 'occupied'
                              ? 'Ocupada'
                              : unit.status === 'maintenance'
                              ? 'Mantenimiento'
                              : 'Reservada'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {canEditUnitRow && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingUnitIndex(index);
                            // Inicializar display con el valor actual de la unidad
                            setUnitM2Display(units[index].m2 > 0 ? units[index].m2.toString() : '');
                          }}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {canDeleteUnits && (
                        <button
                          type="button"
                          onClick={() => removeUnit(index)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Gestión de Documentos */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Documentos</h3>
        <div className="space-y-4">
          {/* Lista de documentos existentes */}
          {documents.length > 0 && (
            <div className="space-y-2">
              {documents.map((doc, index) => (
                <div
                  key={doc.id ?? `new-doc-${index}`}
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                      <p className="text-xs text-gray-500">
                        {doc.fileType?.toUpperCase() || 'DOC'}
                      </p>
                    </div>
                  </div>
                  {canDeletePropertyDocs && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument(index)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar documento"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Formulario para agregar nuevo documento */}
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Agregar Nuevo Documento</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título del Documento
                </label>
                <input
                  type="text"
                  value={newDocument.title}
                  onChange={(e) => setNewDocument(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                  placeholder="Ej: Título de Propiedad"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Archivo
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={documentUploading}
                  onChange={handleDocumentFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent/90 disabled:opacity-50"
                />
                {/* Barra de progreso que se va llenando hasta que termina la subida */}
                {documentUploading && (
                  <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-[width] duration-200 ease-out"
                      style={{ width: `${documentUploadProgress}%` }}
                    />
                  </div>
                )}
                {/* Icono de tipo de archivo cuando terminó de subir */}
                {newDocument.fileUrl && !documentUploadError && !documentUploading && (
                  <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
                    {newDocument.fileType?.toLowerCase() === 'pdf' ? (
                      <FileText className="w-8 h-8 text-red-600 shrink-0" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-blue-600 shrink-0" />
                    )}
                    <span className="text-sm text-green-700">Archivo listo. Escribe el título y pulsa &quot;Agregar Documento&quot;.</span>
                  </div>
                )}
                {documentUploadError && <span className="text-xs text-red-600 mt-1 block">{documentUploadError}</span>}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleAddDocument}
                  disabled={!newDocument.title.trim() || !newDocument.fileUrl}
                  className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:cursor-not-allowed enabled:bg-green-600 enabled:text-white enabled:hover:bg-green-700 disabled:bg-gray-400 disabled:text-gray-200"
                >
                  <Plus className="w-4 h-4" />
                  <span>Agregar Documento</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={
            isSubmitting ||
            (isEditingProperty ? !canUpdate('properties') : !canCreate('properties'))
          }
          className="px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 min-w-[160px] bg-accent text-white hover:bg-accent/90 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {property ? 'Guardando…' : 'Agregando…'}
            </>
          ) : (
            property ? 'Guardar Cambios' : 'Agregar Propiedad'
          )}
        </button>
      </div>
    </form>
  );
};

